import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import LabsAppBar from "@components/LabsAppBar";
import { ActionButton } from '@components/LabsAppBar';
import axios from 'axios';
import { GroupedExtensionEvent } from 'src/types/extension'; // Updated imports
import { ExternalLink, BarChart2, AlertCircle } from 'lucide-react'; // Added AlertCircle
import TableComponent from '@components/shared/TableComponent';
import { ColumnDef } from '@tanstack/react-table'; // Import ColumnDef

const ChromeExtensionPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate(); // Initialize useNavigate
  const [groupedWebsiteReports, setGroupedWebsiteReports] = useState<GroupedExtensionEvent[]>([]); // Changed state name and type
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Removed expandedUrls and toggleExpand as TableComponent will handle row clicks

  const fetchExtensionData = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const url = apiUrl ? `${apiUrl}/api/extension/events` : '/api/extension/events'; // Corrected API endpoint
      
      console.log('Fetching grouped extension data from URL:', url);
      const response = await axios.get<GroupedExtensionEvent[]>(url); // Changed expected response type
      console.log('Grouped data received:', response.data);
      
      setGroupedWebsiteReports(response.data); // Set grouped data
    } catch (err) {
      console.error('Failed to fetch grouped extension data:', err);
      setError('Failed to load data. Please ensure the backend is running and the extension is sending data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExtensionData();
  }, []);

  let breadcrumbs = [];
  let actions: ActionButton[] = [
    { label: 'Refresh', onClick: fetchExtensionData, iconName: 'RefreshCw', variant: 'text' },
  ];
  let pageTitle = '';
  let pageIcon: string | undefined;

  switch (location.pathname) {
    case '/labs/chrome-extension':
      pageTitle = 'Website Scans';
      pageIcon = 'FileChartColumn';
      break;
    default:
      pageTitle = 'Website Scans';
      pageIcon = 'FileChartColumn';
      break;
  }

  breadcrumbs = [{ name: pageTitle, iconName: pageIcon }];

  const handleRowClick = (report: GroupedExtensionEvent) => {
    // Navigate to ScanSummaryPage, passing the domain as a URL parameter
    navigate(`/labs/chrome-extension/${encodeURIComponent(report.domain)}`);
  };

  const websiteReportColumns: ColumnDef<GroupedExtensionEvent>[] = useMemo(() => [
    {
      accessorKey: 'domain',
      header: 'Website Domain',
      cell: ({ row }) => (
        <div className="flex items-center">
          <ExternalLink className="h-4 w-4 mr-2 text-gray-500" />
          <span className="font-medium text-gray-900 dark:text-white">{row.original.domain}</span>
        </div>
      ),
    },
    {
      accessorFn: row => row.detectedTags.length,
      id: 'uniqueTags',
      header: 'Unique Tags',
      cell: ({ row }) => row.original.detectedTags.length,
    },
    {
      accessorFn: row => row.dataLayerEvents.filter(e => e.type === 'track').length,
      id: 'trackEvents',
      header: 'Track Events',
      cell: ({ row }) => row.original.dataLayerEvents.filter(e => e.type === 'track').length,
    },
    {
      accessorFn: row => row.webRequests.filter(req => req.statusCode && req.statusCode >= 400).length,
      id: 'brokenRequests',
      header: 'Broken Requests (400+/500+)',
      cell: ({ row }) => row.original.webRequests.filter(req => req.statusCode && req.statusCode >= 400).length,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end space-x-2">
          <button
            className="text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-300 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click from firing
              handleRowClick(row.original);
            }}
          >
            <BarChart2 className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ], []); // Dependencies for useMemo

  return (
    <div className="flex flex-col h-full">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={actions}
      />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-10 max-w-9xl mx-auto">
          <div className="space-y-8">
            <TableComponent
              title="Scanned Websites"
              data={groupedWebsiteReports}
              columns={websiteReportColumns}
              onRowClick={handleRowClick}
              isLoading={loading}
              isError={!!error}
              errorContent={
                <div className="flex items-center justify-center py-4 text-red-500">
                  <AlertCircle className="h-8 w-8 mr-3" />
                  <span>{error}</span>
                </div>
              }
              buttons={null} // No extra buttons needed for this table
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChromeExtensionPage;
